'use client'

import { StarIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatFirstLetterToUpperCase } from '@/lib/utils/format-util'

/** The gold star marking a control as locked behind a higher plan. */
export function PlanStar({ className }: { className?: string }) {
  return (
    <StarIcon aria-hidden className={cn('fill-ox-gold text-ox-gold size-3 shrink-0', className)} />
  )
}

/** "<feature> is available on <plan>" — `plan` is a catalog `plan_label`, defaulting to Pro. */
export function usePlanLockMessage(): (feature: string, plan?: string) => string {
  const t = useTranslations('billing')
  return (feature, plan) =>
    t('availableOn', { feature, plan: plan ? formatFirstLetterToUpperCase(plan) : 'Pro' })
}

/**
 * Explains a plan-locked control on hover. A disabled control swallows pointer
 * events, so the tooltip hangs off a focusable wrapper around it instead.
 * Unlocked, renders `children` untouched.
 */
export function PlanLockTooltip({
  locked,
  feature,
  plan,
  className,
  children,
}: {
  locked: boolean
  feature: string
  plan?: string
  className?: string
  children: ReactNode
}) {
  const message = usePlanLockMessage()(feature, plan)
  if (!locked) return children

  // Own provider so sweeping the pointer across a list of locked items never
  // chains tooltips open (skipDelay 0): it only shows once the pointer rests.
  // The content is click-through so it can't cover an enabled control.
  return (
    <TooltipProvider delayDuration={600} skipDelayDuration={0} disableHoverableContent>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={message}
            // A menu/popover opening focuses its first item; if that's a locked one, don't pop
            // the tooltip unasked. Keyboard focus (focus-visible) still shows it.
            onFocus={(event) => {
              if (!event.currentTarget.matches(':focus-visible')) event.preventDefault()
            }}
            className={cn('cursor-not-allowed', className)}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent className="pointer-events-none">{message}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
